import { isFilterableColumn } from '../filter/filter-utils'
import type { ComponentSize } from '@zoryn/constants'
import type { GridColumnInfo, GridOptions } from '../types'
import {
  resolveHeaderCellPaddingX,
  resolveHeaderTitleFont,
  resolveSpecialColumnWidth,
} from './grid-size-metrics'
import {
  isGridColumnEditable,
  isGridSpecialColumn,
  resolveGridEditable,
} from './meta'

/** 与 theme-chalk grid 表头样式对齐：font-weight 600 + base 字号 */
export const GRID_HEADER_TITLE_FONT = resolveHeaderTitleFont()

/** header-title inline-flex gap */
const HEADER_TITLE_GAP = 1

const REQUIRED_ICON_WIDTH = 11
const EDIT_ICON_WIDTH = 14
const SORT_ICON_WIDTH = 18
const FILTER_ICON_WIDTH = 16

/** 子像素取整、右边框与 flex 收缩余量 */
const HEADER_MIN_SAFETY_BUFFER = 12

/** 默认 border 模式下 header-cell 的 border-right */
const HEADER_CELL_BORDER_X = 1

let measureCanvas: HTMLCanvasElement | null = null

function getMeasureContext() {
  if (typeof document === 'undefined') {
    return null
  }
  measureCanvas ??= document.createElement('canvas')
  return measureCanvas.getContext('2d')
}

/** 估算表头标题文字宽度（px） */
export function estimateHeaderTitleWidth(
  text: string,
  font = GRID_HEADER_TITLE_FONT
): number {
  if (!text) {
    return 0
  }
  const ctx = getMeasureContext()
  if (ctx) {
    ctx.font = font
    const measured = ctx.measureText(text).width
    if (measured > 0) {
      return Math.ceil(measured)
    }
  }
  let width = 0
  for (const char of text) {
    width += char.charCodeAt(0) > 255 ? 14 : 8
  }
  return width
}

/** 表头除标题文字外的固定占位（图标、内边距、间距） */
export function resolveHeaderChromeWidth(
  column: GridColumnInfo,
  tableEditable = true,
  size?: ComponentSize | null
): number {
  if (isGridSpecialColumn(column)) {
    return resolveSpecialColumnWidth(size)
  }

  let width = resolveHeaderCellPaddingX(size) + HEADER_CELL_BORDER_X
  const editable = isGridColumnEditable(column, tableEditable)
  const showRequired = editable && !!column.required
  const showSort = column.sortable !== false && !!column.field
  const showFilter = isFilterableColumn(column)

  let startWidth = 0
  if (showRequired) {
    startWidth += REQUIRED_ICON_WIDTH
  }
  if (editable) {
    if (startWidth > 0) {
      startWidth += HEADER_TITLE_GAP
    }
    startWidth += EDIT_ICON_WIDTH
  }

  let endWidth = 0
  if (showFilter) {
    endWidth += FILTER_ICON_WIDTH
  }
  if (showSort) {
    if (endWidth > 0) {
      endWidth += HEADER_TITLE_GAP
    }
    endWidth += SORT_ICON_WIDTH
  }

  width += startWidth
  if (startWidth > 0) {
    width += HEADER_TITLE_GAP
  }
  if (endWidth > 0) {
    width += HEADER_TITLE_GAP
  }
  width += endWidth

  return width
}

/** 表头完整展示所需最小列宽 */
export function resolveHeaderMinWidth(
  column: GridColumnInfo,
  tableEditable = true,
  size?: ComponentSize | null
): number {
  if (isGridSpecialColumn(column)) {
    return resolveSpecialColumnWidth(size)
  }
  const title = column.title || column.field || ''
  return (
    estimateHeaderTitleWidth(title, resolveHeaderTitleFont(size)) +
    resolveHeaderChromeWidth(column, tableEditable, size) +
    HEADER_MIN_SAFETY_BUFFER
  )
}

export function isHeaderAutoWidthEnabled(options?: GridOptions) {
  return options?.headerAutoWidth !== false
}

/** 按表头内容抬升列宽 / minWidth，避免标题被 ellipsis；用户指定 width 过窄时同样生效 */
export function applyHeaderMinWidths(
  columns: GridColumnInfo[],
  options?: GridOptions,
  props?: { editable?: boolean },
  size?: ComponentSize | null
) {
  const tableEditable = resolveGridEditable(props, options)
  for (const column of columns) {
    if (isGridSpecialColumn(column)) {
      continue
    }
    const headerMin = resolveHeaderMinWidth(column, tableEditable, size)
    column.headerMinWidth = headerMin
    const currentWidth = Number(column.width) || 0
    const currentMinWidth = Number(column.minWidth) || 80
    column.width = Math.max(currentWidth, headerMin)
    column.minWidth = Math.max(currentMinWidth, headerMin)
  }
  return columns
}
